import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const P  = '#C29500';
const P2 = '#E8C040';

const KudiLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="fp-g" x1="0" y1="0" x2="80" y2="80" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor={P2}/>
        <stop offset="100%" stopColor={P}/>
      </linearGradient>
    </defs>
    <rect width="80" height="80" rx="20" fill="url(#fp-g)"/>
    <rect x="20" y="18" width="9" height="44" rx="4.5" fill="#fff"/>
    <path d="M29 40 L54 20" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M29 40 L54 60" stroke="#fff" strokeWidth="9" strokeLinecap="round"/>
    <path d="M42 56 L54 60 L50 48" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function ForgotPassword() {
  const [email, setEmail]   = useState('');
  const [sent, setSent]     = useState(false);
  const [loading, setLoad]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Enter your email');
    setLoad(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      // Endpoint is intentionally generic; show the same success either way
      setSent(true);
    } finally {
      setLoad(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <KudiLogo size={40}/>
            <span style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>Kudi<span style={{ color: P2 }}>bot</span></span>
          </Link>
        </div>

        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 32 }}>
          {sent ? (
            <div style={{ textAlign: 'center' }}>
              <CheckCircle size={44} style={{ color: P2, margin: '0 auto 14px' }}/>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>Check your email</h1>
              <p style={{ color: '#64748B', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px' }}>
                If an account exists for <b style={{ color: '#cbd5e1' }}>{email}</b>, we've sent a password-reset link. It expires in 1 hour.
              </p>
              <Link to="/login" style={{ color: P2, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 6px' }}>Forgot password?</h1>
              <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 22px' }}>
                Enter your email and we'll send you a reset link.
              </p>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: '#94a3b8', marginBottom: 8, fontWeight: 600 }}>Email Address</label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
                    <input type="email" placeholder="you@example.com"
                      value={email} onChange={e => setEmail(e.target.value)}
                      style={{ width: '100%', boxSizing: 'border-box', background: '#0D0D12', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '12px 14px 12px 42px', color: '#fff', fontSize: 14, outline: 'none' }}/>
                  </div>
                </div>
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} type="submit" disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#0A0A0F', background: `linear-gradient(135deg, ${P2}, ${P})`, opacity: loading ? 0.7 : 1 }}>
                  {loading ? 'Sending...' : <><span>Send reset link</span><ArrowRight size={16}/></>}
                </motion.button>
              </form>
              <div style={{ textAlign: 'center', marginTop: 22 }}>
                <Link to="/login" style={{ color: '#64748B', fontWeight: 600, textDecoration: 'none', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ArrowLeft size={14}/> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
      <style>{`input::placeholder { color: #334155; }`}</style>
    </div>
  );
}
